import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, Headset, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  NavigationMenu, NavigationMenuList, NavigationMenuItem,
  NavigationMenuTrigger, NavigationMenuContent,
} from './ui/navigation-menu';
import { NAV_CATEGORIES, SERVICE_CATALOG, serviceFormHref } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { serviceTitle } from '../i18n/serviceCopy';
import BrandLogo from './BrandLogo';
import LanguageToggle from './LanguageToggle';

const NAV_SHORT_LABEL_FALLBACK = {
  INCOME_TAX: 'Income Tax',
  GST: 'GST',
  COMPANY: 'Startup',
  MCA: 'MCA',
  ACCOUNTING: 'Accounting',
  PAYROLL: 'Payroll',
  TRADEMARK: 'Trademark',
  LICENSE: 'Registrations',
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [navValue, setNavValue] = useState(undefined);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const closeMenu = () => setNavValue(undefined);
  // Radix's NavigationMenu can still ask to change `value` from a hover-intent path in some
  // interaction sequences. Since this is a controlled menu, only honor a value change that was
  // just flagged by an actual trigger click (see allowTriggerClick below) — hover never sets the flag.
  const allowMenuChangeRef = useRef(false);
  const handleMenuValueChange = (next) => {
    // Closing is always allowed (outside click, Escape, re-clicking the open trigger).
    // Opening/switching to a category is only allowed right after a real trigger click.
    if (next && !allowMenuChangeRef.current) return;
    allowMenuChangeRef.current = false;
    if (!next) closeMenu();
    setNavValue(next);
  };
  const allowTriggerClick = () => {
    allowMenuChangeRef.current = true;
  };
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchOpen) return undefined;
    searchInputRef.current?.focus();
    const onDocClick = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [searchOpen]);

  // Desktop header tightens in steps (lg → 1440px → 1640px) so logo, menu and actions always fit on one row
  // at the 97% page scale set in App.css.
  const linkClass = ({ isActive }) =>
    `inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-md px-1.5 min-[1440px]:px-2 min-[1640px]:px-2.5 py-0 text-[13px] min-[1640px]:text-[14px] font-semibold leading-none transition-colors hover:bg-teal-50/60 hover:text-[#14A098] ${isActive ? 'text-[#14A098]' : 'text-[#0f2540]'}`;

  const catTriggerClass =
    'inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-md bg-transparent px-1.5 min-[1440px]:px-2 min-[1640px]:px-2.5 py-0 text-[13px] min-[1640px]:text-[14px] font-semibold leading-none text-[#0f2540] shadow-none [&>svg]:ml-0.5 min-[1640px]:[&>svg]:ml-1 hover:bg-teal-50/60 hover:text-[#14A098] focus:bg-teal-50/60 data-[state=open]:bg-teal-50/60 data-[state=open]:text-[#14A098] data-[state=open]:hover:bg-teal-50/60';

  const actionBtnClass = 'h-9 w-9 p-0 min-[1440px]:h-10 text-[12px] min-[1440px]:text-sm rounded-full border-2 border-[#14A098] whitespace-nowrap shrink-0';

  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);
  const catDesc = (cat) => t(`category.${cat.category}_desc`, cat.desc);
  const catShortLabel = (cat) => t(`nav.cat_short.${cat.category}`, NAV_SHORT_LABEL_FALLBACK[cat.category] || cat.label);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return SERVICE_CATALOG
      .filter((svc) => serviceTitle(svc.code, t, svc.title).toLowerCase().includes(q))
      .slice(0, 7);
  }, [query, t]);

  const goToService = (code) => {
    setSearchOpen(false);
    setQuery('');
    navigate(serviceFormHref(code));
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchResults[0]) {
      goToService(searchResults[0].code);
    } else {
      setSearchOpen(false);
      navigate('/all-services');
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'}`}>
      <nav className="w-full max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-4 min-[1440px]:px-6 min-[1640px]:px-8 min-h-[4.75rem] sm:min-h-[5rem] lg:min-h-[5rem] py-2 lg:py-2 flex items-center justify-between gap-2 sm:gap-3 md:gap-4 lg:gap-2 min-[1440px]:gap-3 min-[1640px]:gap-4 min-w-0">
        <BrandLogo variant="default" className="shrink min-w-0 max-w-[58vw] sm:max-w-[300px]" />

        <div className="hidden lg:flex flex-wrap items-center justify-center gap-x-1 min-[1440px]:gap-x-1.5 gap-y-1 min-w-0 flex-1 min-[1440px]:px-1">
          <NavigationMenu
            value={navValue}
            onValueChange={handleMenuValueChange}
            className="max-w-max flex-none"
          >
            <NavigationMenuList className="justify-start flex-wrap gap-x-0.5 gap-y-1 space-x-0">
              {NAV_CATEGORIES.map((cat) => {
                const count = cat.services.length;
                const cols = count > 18 ? 4 : count > 12 ? 3 : count > 6 ? 2 : 1;
                const widthClass = { 1: 'w-[280px]', 2: 'w-[440px]', 3: 'w-[640px]', 4: 'w-[860px]' }[cols];
                const gridClass = { 1: '', 2: 'grid grid-cols-2 gap-x-3', 3: 'grid grid-cols-3 gap-x-3', 4: 'grid grid-cols-4 gap-x-3' }[cols];
                return (
                  <NavigationMenuItem key={cat.category} value={cat.category}>
                    <NavigationMenuTrigger className={catTriggerClass} onClick={allowTriggerClick}>
                      {catShortLabel(cat)}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className={`p-4 ${widthClass} max-w-[90vw]`}>
                        <div className="mb-2 pb-2 border-b border-gray-100">
                          <div className="text-sm font-bold text-[#0f2540]">{catLabel(cat)}</div>
                          <div className="text-xs text-gray-500 leading-snug mt-0.5">{catDesc(cat)}</div>
                        </div>
                        <div className={gridClass}>
                          {cat.services.map((svc) => (
                            <Link
                              key={svc.code}
                              to={serviceFormHref(svc.code)}
                              onClick={closeMenu}
                              className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-[#0f2540] hover:bg-teal-50 hover:text-[#14A098] transition-colors group"
                            >
                              <svc.icon className="w-4 h-4 text-[#14A098] shrink-0" />
                              <span className="leading-snug">{serviceTitle(svc.code, t, svc.title)}</span>
                            </Link>
                          ))}
                        </div>
                        <Link
                          to={`/all-services?cat=${encodeURIComponent(cat.category)}`}
                          onClick={closeMenu}
                          className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1 text-xs font-semibold text-[#14A098] hover:text-[#0e7a74] px-2"
                        >
                          {t('nav.view_n_services', 'View {n} services', { n: cat.services.length })} <ArrowRight className="w-3 h-3" />
                        </Link>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              })}
              <li className="flex items-center">
                <NavLink to="/referral" className={linkClass}>{t('nav.referral', 'Refer & Earn')}</NavLink>
              </li>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="hidden lg:flex items-center justify-end gap-1.5 min-[1440px]:gap-2 shrink-0 min-w-0">
          <LanguageToggle compact className="shrink-0" />
          <div ref={searchWrapRef} className="relative shrink-0">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label={t('nav.search', 'Search services')}
              className={`h-9 w-9 min-[1440px]:h-10 min-[1440px]:w-10 inline-flex items-center justify-center rounded-full border transition-colors ${searchOpen ? 'bg-teal-50 border-[#14A098] text-[#14A098]' : 'border-gray-200 text-[#0f2540] hover:border-[#14A098]/40 hover:text-[#14A098]'}`}
            >
              {searchOpen ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
            </button>
            {searchOpen && (
              <div className="absolute right-0 top-[calc(100%+10px)] w-[340px] max-w-[90vw] rounded-2xl border border-gray-100 bg-white shadow-xl p-3 z-50">
                <form onSubmit={submitSearch}>
                  <Input
                    ref={searchInputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('nav.search_placeholder', 'Search GST, ITR, company reg...')}
                    className="h-10"
                  />
                </form>
                {query.trim() && (
                  <div className="mt-2 max-h-72 overflow-y-auto space-y-0.5">
                    {searchResults.length === 0 && (
                      <div className="text-xs text-gray-500 px-2 py-3 text-center">{t('nav.search_empty', 'No matching services')}</div>
                    )}
                    {searchResults.map((svc) => (
                      <button
                        key={svc.code}
                        type="button"
                        onClick={() => goToService(svc.code)}
                        className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-left text-[#0f2540] hover:bg-teal-50 hover:text-[#14A098] transition-colors"
                      >
                        <svc.icon className="w-4 h-4 text-[#14A098] shrink-0" />
                        <span className="truncate">{serviceTitle(svc.code, t, svc.title)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* lg: icon-only · min-[1440px]: Call label · min-[1640px]: both labels */}
          <a href="tel:+919346004430" className="shrink-0" aria-label={t('nav.call_now', 'Call Now')} title={t('nav.call_now', 'Call Now')}>
            <Button tabIndex={-1} className={`${actionBtnClass} min-[1440px]:w-auto min-[1440px]:px-4 bg-[#14A098] hover:bg-[#0e7a74] text-white shadow-md shadow-[#14A098]/30`}>
              <Phone />
              <span className="hidden min-[1440px]:inline">{t('nav.call_now', 'Call Now')}</span>
            </Button>
          </a>
          <Button
            onClick={() => navigate('/contact')}
            variant="outline"
            aria-label={t('nav.customer_support', 'Customer Support')}
            title={t('nav.customer_support', 'Customer Support')}
            className={`${actionBtnClass} min-[1440px]:w-10 min-[1640px]:w-auto min-[1640px]:px-4 text-[#14A098] hover:bg-[#14A098] hover:text-white shadow-sm`}
          >
            <Headset />
            <span className="hidden min-[1640px]:inline">{t('nav.customer_support', 'Customer Support')}</span>
          </Button>
        </div>

        <div className="lg:hidden flex items-center gap-2 shrink-0 ml-auto">
          <LanguageToggle compact className="shrink-0" />
          <Link
            to="/all-services"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-[#0f2540]"
            aria-label={t('nav.search', 'Search services')}
          >
            <Search className="w-4 h-4" />
          </Link>
          <a
            href="tel:+919346004430"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#14A098] text-white shadow-sm shadow-[#14A098]/30 hover:bg-[#0e7a74] transition-colors"
            aria-label={t('nav.mobile_call', 'Call')}
          >
            <Phone className="w-4 h-4" />
          </a>
          <Button
            onClick={() => navigate('/contact')}
            variant="outline"
            className="h-9 px-3 text-xs border-2 border-[#14A098] text-[#14A098] rounded-full hover:bg-[#14A098] hover:text-white"
          >
            <Headset className="w-3.5 h-3.5 mr-1.5" />
            {t('nav.mobile_support', 'Support')}
          </Button>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
